// ProductForm.js
import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  TextField,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Alert,
  FormHelperText,
  Typography,
} from "@mui/material";
import { addProduct, updateProduct } from "../utils/api";

const ProductForm = ({
  branch,
  product,
  done,
  setSnackbarMessage,
  setSnackbarSeverity,
  handleOpenSnackbar,
}) => {
  const { handleSubmit, control, setValue, reset } = useForm();

  const onSubmit = async (data) => {
    try {
      if (product) {
        data.product = product._id;
        const response = await updateProduct(data);
        if (response.status === 200) {
          setSnackbarMessage(response.data.message || "Successful");
          setSnackbarSeverity("success");
          handleOpenSnackbar();
          reset();
          done();
        } else {
          setSnackbarMessage(response.data.message || "Failed");
          setSnackbarSeverity("error");
          handleOpenSnackbar();
        }
      } else {
        data.br = branch;
        const response = await addProduct(data);
        if (response.status === 201) {
          setSnackbarMessage(response.data.message || "Successful");
          setSnackbarSeverity("success");
          handleOpenSnackbar();
          reset();
          done();
        } else {
          setSnackbarMessage(response.data.message || "Failed");
          setSnackbarSeverity("error");
          handleOpenSnackbar();
        }
      }
    } catch (error) {
      console.error("Error:", error);
      setSnackbarMessage(
        error?.response?.data?.message ||
          "An error occurred. Please try again later."
      );
      setSnackbarSeverity("error");
      handleOpenSnackbar();
    }
  };

  const categories = [
    { value: 1, label: "Detergent" },
    { value: 2, label: "Fabric Conditioner" },
    { value: 3, label: "Bleach" },
  ];

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          maxWidth: 400,
        }}
      >
        {!product && (
          <Typography variant="caption">
            Note: Category cannot be edited after saving.
          </Typography>
        )}
        <Controller
          name="nm"
          control={control}
          defaultValue={product ? product.nm : ""}
          rules={{
            required: "Product name is required",
            validate: (value) => {
              if (value.length > 50) {
                return "Must not exceed to 50 characters";
              }

              return true;
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              size="small"
              {...field}
              label="Product Name"
              fullWidth
              error={!!error}
              helperText={error ? error.message : null}
              autoComplete="off"
            />
          )}
        />

        <Controller
          name="price"
          control={control}
          defaultValue={product ? product.price : ""}
          rules={{
            required: "Price is required",
            validate: (value) => {
              const numberValue = parseFloat(value);

              if (numberValue <= 0) {
                return "Price must be greater than 0";
              }

              if (numberValue > 999) {
                return "Price must not exceed 999";
              }

              if (!Number.isInteger(numberValue)) {
                return "Price must be a whole number";
              }

              return true;
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              size="small"
              {...field}
              label="Price"
              type="number"
              fullWidth
              error={!!error}
              helperText={error ? error.message : null}
              autoComplete="off"
            />
          )}
        />

        {!product ? (
          <Controller
            name="cat"
            control={control}
            defaultValue=""
            rules={{ required: "Category is required" }}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth size="small" error={!!error}>
                <InputLabel>Category</InputLabel>
                <Select {...field} label="Category">
                  {categories.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </Select>
                {error && <FormHelperText>{error.message}</FormHelperText>}
              </FormControl>
            )}
          />
        ) : (
          ""
        )}
        <Box textAlign="right">
          <Button type="submit" variant="contained" color="primary">
            Save
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default ProductForm;
